import {
  MY_APPS_CATEGORIES,
  type MyAppsCatalogItem,
  type MyAppsEntryCategory,
  type MyAppsExternalApp,
  type MyAppsRegistryEntry,
  type MyAppsSearchResult
} from './myAppsTypes';

export const MY_APPS_IDLE_RECENT_LIMIT = 6;
export const MY_APPS_SEARCH_RESULT_LIMIT = 12;
export const CUSTOM_APP_LAUNCH_PREFIX = '/apps/link/';

export function customAppLaunchPath(slug: string): string {
  return `${CUSTOM_APP_LAUNCH_PREFIX}${encodeURIComponent(slug)}`;
}

export function parseCustomAppLaunchSlug(pathname: string): string | null {
  if (!pathname.startsWith(CUSTOM_APP_LAUNCH_PREFIX)) return null;
  const slug = decodeURIComponent(
    pathname.slice(CUSTOM_APP_LAUNCH_PREFIX.length).split('/')[0] ?? ''
  );
  return slug.trim() ? slug : null;
}

function asCategory(value: string): MyAppsEntryCategory {
  return MY_APPS_CATEGORIES.includes(value as MyAppsEntryCategory)
    ? (value as MyAppsEntryCategory)
    : 'Utilities';
}

export function toExternalSearchResult(app: MyAppsExternalApp): MyAppsSearchResult {
  return {
    id: app.id,
    title: app.title,
    description: app.description,
    path: app.path || customAppLaunchPath(app.id),
    icon: app.icon,
    searchText: [app.title, app.description, app.category, app.id].join(' ').toLocaleLowerCase(),
    kind: 'link',
    category: asCategory(app.category),
    url: app.url
  };
}

/**
 * Joins what the user is permitted to see (server catalog) with how it is presented
 * (client registry). Catalog ids with no registry entry have no route, so they are skipped.
 */
export function buildMyAppsEntries(
  catalog: readonly MyAppsCatalogItem[],
  externalApps: readonly MyAppsExternalApp[],
  registry: readonly MyAppsRegistryEntry[]
): readonly MyAppsSearchResult[] {
  const byId = new Map(registry.map((entry) => [entry.id.toLowerCase(), entry]));
  const builtIn = catalog.flatMap((item) => {
    const presentation = byId.get(item.id.toLowerCase());
    if (!presentation) return [];
    return [
      {
        id: item.id,
        title: item.title,
        description: item.description,
        path: presentation.path,
        icon: presentation.icon,
        searchText: [item.title, item.description, item.category, item.id, ...(presentation.keywords ?? [])]
          .join(' ')
          .toLocaleLowerCase(),
        kind: 'app' as const,
        category: asCategory(item.category)
      }
    ];
  });
  return [...builtIn, ...externalApps.map(toExternalSearchResult)];
}

export function searchMyApps(
  entries: readonly MyAppsSearchResult[],
  query: string
): readonly MyAppsSearchResult[] {
  const tokens = query.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return entries;

  return entries
    .map((entry) => ({ entry, score: scoreEntry(entry, tokens) }))
    .filter((candidate) => candidate.score > 0)
    .sort(
      (left, right) => right.score - left.score || left.entry.title.localeCompare(right.entry.title)
    )
    .map((candidate) => candidate.entry);
}

function scoreEntry(entry: MyAppsSearchResult, tokens: readonly string[]): number {
  if (!tokens.every((token) => entry.searchText.includes(token))) return 0;
  const normalizedTitle = entry.title.toLocaleLowerCase();
  let score = 1;
  let titleMatched = false;
  for (const token of tokens) {
    if (normalizedTitle === token) {
      score += 20;
      titleMatched = true;
    } else if (normalizedTitle.startsWith(token)) {
      score += 10;
      titleMatched = true;
    } else if (normalizedTitle.includes(token)) {
      score += 5;
      titleMatched = true;
    } else score += 1;
  }
  if (!titleMatched && entry.path.split('/').filter(Boolean).length > 2) score += 2;
  return score;
}

/**
 * Which entry the current route counts as, so visiting an app records it as recent.
 * Longest path wins so /apps/document-editor does not shadow a nested route.
 */
export function resolveRecentMyAppId(
  pathname: string,
  entries: readonly MyAppsSearchResult[]
): string | null {
  const customSlug = parseCustomAppLaunchSlug(pathname);
  if (customSlug) {
    const match = entries.find((entry) => entry.id.toLowerCase() === customSlug.toLowerCase());
    if (match) return match.id;
  }

  let best: MyAppsSearchResult | null = null;
  for (const entry of entries) {
    if (entry.kind === 'link') continue;
    if (pathname !== entry.path && !pathname.startsWith(`${entry.path}/`)) continue;
    if (!best || entry.path.length > best.path.length) best = entry;
  }
  return best?.id ?? null;
}

export interface LauncherEntry {
  id: string;
}

export interface IdleLauncherSections<T extends LauncherEntry> {
  pinned: readonly T[];
  recent: readonly T[];
  items: readonly T[];
}

/** Idle Ctrl+K list: pinned first, then recent apps that are not already pinned. */
export function buildIdleLauncherSections<T extends LauncherEntry>(
  pinned: readonly T[],
  recent: readonly T[],
  recentLimit = MY_APPS_IDLE_RECENT_LIMIT
): IdleLauncherSections<T> {
  const pinnedIds = new Set(pinned.map((entry) => entry.id.toLowerCase()));
  const recentOnly = recent
    .filter((entry) => !pinnedIds.has(entry.id.toLowerCase()))
    .slice(0, recentLimit);
  return {
    pinned,
    recent: recentOnly,
    items: [...pinned, ...recentOnly]
  };
}

export function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
}

export function openExternalAppUrl(url: string): boolean {
  if (!isHttpUrl(url)) return false;
  window.open(url, '_blank', 'noopener,noreferrer');
  return true;
}

export function isExternalMyAppsEntry(entry: { kind?: string; url?: string }): boolean {
  return entry.kind === 'link' || isHttpUrl(entry.url ?? '');
}

/** External apps open in a new tab; built-in apps navigate in place. */
export function launchMyAppsEntry(
  entry: Pick<MyAppsSearchResult, 'id' | 'kind' | 'path' | 'url'>,
  navigate: (path: string) => void
): boolean {
  if (isExternalMyAppsEntry(entry)) {
    return openExternalAppUrl(entry.url ?? '');
  }
  navigate(entry.path);
  return true;
}
